import React from "react";
import clsx from "clsx";
import { useIntl } from "react-intl";

import { PlatformDescriptionStatusDTO } from "@app/api/generated";
import { ClickableComponent, IconComponent } from "@app/core";
import { StatusToggleLabel } from "@app/components/status-toggle-label/status-toggle-label";
import { getPlatformName, orderPlatformsByKeys } from "@app/util/platform-name";
import { useProcessedDate } from "@app/util/use-processed-date";

import AutoRenew from "@assets/icons/rounded/auto-renew.svg";

import styles from "@app/styles/dashboard/platform-sidebar.module.scss";

interface IPlatformSidebarProps {
  platforms: PlatformDescriptionStatusDTO[];
  anyRunning: boolean;
  onGeneratePlatform: (platformId: number) => void;
  onToggleEnabled: (platformId: number, enabled: boolean) => void;
  onOpenSettings: () => void;
}

const PlatformSidebar = ({
  platforms,
  anyRunning,
  onGeneratePlatform,
  onToggleEnabled,
  onOpenSettings
}: IPlatformSidebarProps): JSX.Element => {
  const intl = useIntl();
  const formatProcessedDate = useProcessedDate();

  const orderedPlatforms = orderPlatformsByKeys(platforms, (platform) => platform.productDescriptionEnabled);

  return (
    <aside className={styles.sidebar}>
      <div className={styles.header}>
        <ClickableComponent
          buttonType="button"
          variant="secondary"
          title={intl.formatMessage({ id: "productDescription.sidebar.promptSettings" })}
          onClick={onOpenSettings}
        />
      </div>

      <ul className={styles.platformList}>
        {orderedPlatforms.map((platform) => {
          const lastRun = platform.lastRun?.finishedAt
            ? formatProcessedDate(new Date(platform.lastRun.finishedAt))
            : "—";

          return (
            <li key={platform.platformId} className={styles.platform}>
              <div className={styles.platformTop}>
                <span className={styles.platformName}>{getPlatformName(intl, platform.platformId, platform.name)}</span>

                <div className={styles.platformActions}>
                  <StatusToggleLabel
                    active={platform.productDescriptionEnabled}
                    disabled={platform.isRunning || anyRunning}
                    onClick={() => onToggleEnabled(platform.platformId, !platform.productDescriptionEnabled)}
                    title={intl.formatMessage({
                      id: platform.productDescriptionEnabled
                        ? "productDescription.platform.deactivateTooltip"
                        : "productDescription.platform.activateTooltip"
                    })}
                  />
                  <button
                    type="button"
                    className={clsx(styles.smallButton, styles.editButton)}
                    onClick={() => onGeneratePlatform(platform.platformId)}
                    disabled={platform.isRunning || !platform.productDescriptionEnabled || anyRunning}
                    title={intl.formatMessage({ id: "productDescription.platform.generateTooltip" })}
                  >
                    <IconComponent icon={AutoRenew} size="16px" />
                  </button>
                </div>
              </div>

              <dl className={styles.meta}>
                <div>
                  <dt>{intl.formatMessage({ id: "productDescription.sidebar.lastRun" })}</dt>
                  <dd>{lastRun}</dd>
                </div>
              </dl>
            </li>
          );
        })}
      </ul>
    </aside>
  );
};

export { PlatformSidebar };
